import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usePlan } from "@/contexts/PlanContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, UserPlus, Sparkles, AlertTriangle, Trash2, Eye, EyeOff } from "lucide-react";

export default function OrgUsers() {
  const { plan, refresh: refreshPlan } = usePlan();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "", password: "Demo@123" });

  const load = async () => {
    const { data } = await api.get("/org/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const enabled = plan?.enabled_roles || [];
  const quotaFor = (code) => enabled.find((r) => r.code === code);
  const remainingFor = (code) => {
    const r = quotaFor(code);
    if (!r) return 0;
    return Math.max(0, r.max - r.used);
  };

  const submit = async () => {
    try {
      await api.post("/org/users", { ...form, email: form.email.toLowerCase() });
      toast.success(`Created ${form.full_name}`);
      setOpen(false);
      setForm({ email: "", full_name: "", role: "", password: "Demo@123" });
      await Promise.all([load(), refreshPlan()]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.code === "PLAN_LIMIT" || detail.code === "PLAN_ROLE_NOT_INCLUDED")) {
        toast.error(detail.message, {
          description: "Upgrade your plan to add more.",
          action: { label: "Upgrade", onClick: () => window.open(detail.upgrade_url || "/portals/organization", "_blank") },
          duration: 8000,
        });
      } else {
        toast.error(detail || "Failed to create user");
      }
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.full_name}?`)) return;
    try {
      await api.delete(`/org/users/${u.id}`);
      toast.success("User removed");
      await Promise.all([load(), refreshPlan()]);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const togglePriceAccess = async (u) => {
    try {
      const next = !(u.price_access);
      await api.patch(`/org/users/${u.id}/price-access`, { price_access: next });
      toast.success(next ? "Price access enabled" : "Price access disabled");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="org-users-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Users & credentials</h1>
          <p className="text-muted-foreground">Create accounts for your team — assignments are gated by your plan headcount.</p>
          {plan ? (
            <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold">
              Plan · {plan.plan_name || plan.plan_code} · {plan.days_remaining} days remaining
            </div>
          ) : null}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="org-user-create-btn"><UserPlus className="w-4 h-4 mr-1" /> New user</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2"><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="org-user-name" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="org-user-email" /></div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="org-user-role"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                  <SelectContent>
                    {enabled.map((r) => {
                      const left = Math.max(0, r.max - r.used);
                      return (
                        <SelectItem key={r.code} value={r.code} disabled={left === 0}>
                          {r.name} · {r.used}/{r.max} used {left === 0 ? "· FULL" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {form.role && remainingFor(form.role) === 0 ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs flex items-center gap-2" data-testid="org-user-quota-warn">
                    <AlertTriangle className="w-4 h-4" /> Headcount limit reached. <Link to={plan?.upgrade_url || "/portals/organization"} className="font-bold underline">Upgrade plan</Link>
                  </div>
                ) : null}
              </div>
              <div className="space-y-2"><Label>Initial password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <Button onClick={submit} data-testid="org-user-save" disabled={!form.role || !form.email || !form.full_name}>Create user</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Plan quota grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {enabled.map((r) => {
          const left = r.max - r.used;
          const pct = Math.min(100, (r.used / r.max) * 100);
          return (
            <div key={r.code} className="surface-card p-4" data-testid={`quota-card-${r.code}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wider">{r.name}</div>
                <Badge variant="outline" className="font-mono text-[10px]">{r.used}/{r.max}</Badge>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className={`h-full ${left === 0 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] mt-2 text-muted-foreground">{left === 0 ? "Headcount full — upgrade to add more" : `${left} seat${left === 1 ? "" : "s"} remaining`}</div>
            </div>
          );
        })}
        {enabled.length === 0 ? (
          <div className="surface-card p-6 text-sm text-muted-foreground" data-testid="quota-empty">No roles included in this plan.</div>
        ) : null}
      </div>

      {/* Users table */}
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Price access</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium text-sm">{u.full_name}</TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{u.role}</Badge></TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{u.account_status}</Badge></TableCell>
                <TableCell>
                  {u.role === "ORG_ADMIN" ? (
                    <Badge variant="outline" className="font-mono text-[10px] bg-emerald-50 text-emerald-900 border-emerald-200">FULL · admin</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant={u.price_access ? "default" : "outline"}
                      onClick={() => togglePriceAccess(u)}
                      data-testid={`user-price-access-${u.id}`}
                      className="h-7 text-[11px]"
                    >
                      {u.price_access ? <><Eye className="w-3 h-3 mr-1" /> ON</> : <><EyeOff className="w-3 h-3 mr-1" /> OFF</>}
                    </Button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {u.role !== "ORG_ADMIN" ? (
                    <Button size="sm" variant="ghost" onClick={() => removeUser(u)} data-testid={`user-delete-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No users yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
